import React from 'react';
import type { AxonEvent } from '@reflex/shared';
import type {
  TurnBlock,
  ChatMessage,
  ChatTokenUsage,
  SystemInitBlock,
  ImageBlock,
  ThinkingBlock,
} from './types.js';
import { TurnBlockRenderer } from './TurnBlockRenderers.js';
import { SystemInitPopoverContent, rendersNothing } from './turn-blocks/index.js';
import { AgentAvatar } from '@reflex/ui/components/agent/AgentAvatar';
import { InteractiveAvatar } from '@reflex/ui/components/shared/InteractiveAvatar';
import { getModelDisplayName } from '@reflex/ui/lib/model-icons';
import { formatTokenCount, formatDateTimeWithSeconds } from '@reflex/ui/lib/format';
import {
  WorkingIndicator,
  SHIMMER_TEXT_CLASS,
} from '@reflex/ui/components/content/WorkingIndicator';
import { StackedImages, type StackImage } from '@reflex/ui/components/content/StackedImages';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { EventDetailTooltip } from '@reflex/ui/components/stream/EventMeta';
import { ChevronDown, ChevronRight, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { Button } from '@reflex/ui/components/ui/button';
import { CopyButton as SharedCopyButton } from '@reflex/ui/components/shared/CopyButton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { buildTurnCopyHtml, buildTurnCopyText } from './turn-copy.js';
import { collapseReadBeforeWriteRetries } from './read-before-write.js';
import { estimateCodexTurnCostUsd } from './codex-cost-estimate.js';

const TOOL_GROUP_VISIBLE = 3;
// Of the visible slots in a collapsed tool run, how many come from the start
// vs. the end. Keeping the first block visible means a run's leading action
// (e.g. a branch-setup Bash call ahead of a string of file edits) is never
// buried behind "N more" — a tail-only slice would hide it entirely until
// the user expands.
const TOOL_GROUP_HEAD = 1;
const TOOL_GROUP_TAIL = TOOL_GROUP_VISIBLE - TOOL_GROUP_HEAD;

// A collapsed thinking run always leaves its most recent block on screen, so
// folding only pays for itself once at least two blocks would be hidden —
// below that the toggle costs as much vertical space as the row it replaces.
const THINKING_RUN_VISIBLE = 1;
const THINKING_RUN_MIN = THINKING_RUN_VISIBLE + 2;

const TOOL_GROUP_TOGGLE_CLASS =
  'my-1 ml-2 h-auto gap-1.5 rounded-full border border-border/30 bg-muted/20 px-2.5 py-1 text-xs font-normal text-muted-foreground/60 hover:border-border/50 hover:bg-muted/40 hover:text-muted-foreground dark:hover:bg-muted/40';

const CONTENT_BLOCK_TYPES = new Set<TurnBlock['type']>(['text', 'image', 'resource_link']);

export interface VisualGroup {
  id: string;
  blocks: TurnBlock[];
}

/** @internal exported for testing */
export function buildVisualGroups(blocks: TurnBlock[]): VisualGroup[] {
  const groups: VisualGroup[] = [];
  let current: TurnBlock[] = [];

  for (const block of blocks) {
    // Start a new group only when a content block follows a non-content block.
    // Consecutive content blocks stay in the same group so they render as one
    // message bubble instead of being split apart.
    const isContent = CONTENT_BLOCK_TYPES.has(block.type);
    const lastIsContent =
      current.length > 0 && CONTENT_BLOCK_TYPES.has(current[current.length - 1].type);
    if (isContent && current.length > 0 && !lastIsContent) {
      groups.push({ id: current[0].id, blocks: current });
      current = [];
    }
    current.push(block);
  }
  if (current.length > 0) {
    groups.push({ id: current[0].id, blocks: current });
  }
  return groups;
}

type SegmentItem =
  | { kind: 'block'; block: TurnBlock }
  | { kind: 'run'; blocks: TurnBlock[] }
  | { kind: 'thinking_run'; blocks: ThinkingBlock[] };

type ContentSegment =
  | { kind: 'images'; blocks: ImageBlock[] }
  | { kind: 'block'; block: TurnBlock };

function segmentContentBlocks(blocks: TurnBlock[]): ContentSegment[] {
  const result: ContentSegment[] = [];
  let imageBuffer: ImageBlock[] = [];

  for (const block of blocks) {
    if (block.type === 'image') {
      imageBuffer.push(block as ImageBlock);
    } else {
      if (imageBuffer.length > 0) {
        result.push({ kind: 'images', blocks: imageBuffer });
        imageBuffer = [];
      }
      result.push({ kind: 'block', block });
    }
  }

  if (imageBuffer.length > 0) {
    result.push({ kind: 'images', blocks: imageBuffer });
  }

  return result;
}

/**
 * A finished (non-streaming) `thinking` block — the only shape eligible to be
 * swept into a collapsed run. The currently-streaming block (`isActive`)
 * keeps its own live "Thinking…" shimmer and is never folded in.
 */
function isCollapsibleThinking(block: TurnBlock): block is ThinkingBlock {
  return block.type === 'thinking' && !block.isActive;
}

/** A block that rides along inside a run of tool calls without breaking it. */
function isRunFiller(block: TurnBlock): boolean {
  return block.type === 'agent_log';
}

function blockHasEvent(block: TurnBlock, eventId: string | undefined): boolean {
  if (!eventId) return false;
  if (block.sourceEvent?.id === eventId) return true;
  return block.type === 'agent_log' && block.lines.some((line) => line.sourceEvent?.id === eventId);
}

// TODO: this only collapses consecutive `thinking` blocks into a counted
// "Thinking ×N" row. The same counting could extend to other repeated block
// kinds (e.g. a string of Read or Search calls) later; scoped to `thinking`
// for now since that is the noisiest repeat case in practice.
function segmentBlocks(blocks: TurnBlock[]): SegmentItem[] {
  const result: SegmentItem[] = [];
  let i = 0;
  while (i < blocks.length) {
    if (blocks[i].type === 'tool_call') {
      const start = i;
      // A log line the agent wrote *while* working sits between two tool calls
      // without interrupting the sequence, so it extends the run rather than
      // ending it — otherwise a harness that logs continuously (Codex) shatters
      // a 12-call run into stretches too short to collapse, and the turn shows
      // every row uncollapsed. Trailing logs fall back out: a run should end on
      // the action it ended on, since that is what the collapsed view keeps.
      while (i < blocks.length && (blocks[i].type === 'tool_call' || isRunFiller(blocks[i]))) i++;
      while (i > start && isRunFiller(blocks[i - 1])) i--;
      const run = blocks.slice(start, i);
      const toolCount = run.filter((block) => block.type === 'tool_call').length;
      if (toolCount > TOOL_GROUP_VISIBLE) {
        result.push({ kind: 'run', blocks: run });
      } else {
        for (const b of run) result.push({ kind: 'block', block: b });
      }
    } else if (isCollapsibleThinking(blocks[i])) {
      const start = i;
      while (i < blocks.length && isCollapsibleThinking(blocks[i])) i++;
      const run = blocks.slice(start, i) as ThinkingBlock[];
      if (run.length >= THINKING_RUN_MIN) {
        result.push({ kind: 'thinking_run', blocks: run });
      } else {
        for (const b of run) result.push({ kind: 'block', block: b });
      }
    } else {
      result.push({ kind: 'block', block: blocks[i] });
      i++;
    }
  }
  return result;
}

function CollapsedToolRun({
  blocks,
  isLive,
  workingLabel,
  agentId,
  highlightEventId,
}: {
  blocks: TurnBlock[];
  isLive?: boolean;
  workingLabel?: string;
  agentId?: string;
  highlightEventId?: string;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const toolCount = blocks.filter((block) => block.type === 'tool_call').length;
  const hiddenCount = toolCount - TOOL_GROUP_VISIBLE;
  // Collapsed view keeps the run's first block visible alongside its most
  // recent ones (not just the tail), so a leading action never disappears
  // behind "N more" unless the user expands.
  const toolIndexes = blocks.flatMap((block, index) => (block.type === 'tool_call' ? [index] : []));
  const headEnd = toolIndexes[TOOL_GROUP_HEAD - 1] + 1;
  const tailStart = toolIndexes[toolCount - TOOL_GROUP_TAIL];
  const headBlocks = blocks.slice(0, headEnd);
  const tailBlocks = blocks.slice(tailStart);
  React.useEffect(() => {
    const hiddenBlocks = blocks.slice(headEnd, tailStart);
    if (hiddenBlocks.some((block) => blockHasEvent(block, highlightEventId))) setExpanded(true);
  }, [blocks, headEnd, highlightEventId, tailStart]);

  // While the run is live the toggle doubles as the turn's `Working…` row: it
  // already trails every visible action, which is where the "still going"
  // signal belongs, and merging the two keeps a live run one row tall instead
  // of stacking a counter chip and a status line. {@link VisualGroupView}
  // leaves the standalone row out for exactly this case.
  const toggle = expanded ? (
    <Button
      variant="ghost"
      onClick={() => setExpanded(false)}
      className={TOOL_GROUP_TOGGLE_CLASS}
      data-testid="tool-group-toggle"
      aria-expanded={true}
      aria-label={`Hide ${hiddenCount} tool calls`}
    >
      <ChevronDown className="size-3 shrink-0" />
      <span>Show less</span>
    </Button>
  ) : isLive ? (
    <Button
      variant="ghost"
      onClick={() => setExpanded(true)}
      className={TOOL_GROUP_TOGGLE_CLASS}
      data-testid="tool-group-toggle"
      aria-expanded={false}
      aria-label={`Show ${hiddenCount} more tool calls`}
    >
      <Spinner size="xs" className="text-muted-foreground/50" />
      <span className={SHIMMER_TEXT_CLASS}>{workingLabel ?? 'Working…'}</span>
      <span className="text-muted-foreground/30">&middot;</span>
      <span>{hiddenCount} more</span>
    </Button>
  ) : (
    <Button
      variant="ghost"
      onClick={() => setExpanded(true)}
      className={TOOL_GROUP_TOGGLE_CLASS}
      data-testid="tool-group-toggle"
      aria-expanded={false}
      aria-label={`Show ${hiddenCount} more tool calls`}
    >
      <ChevronRight className="size-3 shrink-0" />
      <span>{hiddenCount} more</span>
    </Button>
  );

  if (expanded) {
    return (
      <>
        {blocks.map((block, i, arr) => (
          <TurnBlockRenderer
            key={block.id}
            block={block}
            agentId={agentId}
            isLive={isLive}
            isLastBlock={isLive && i === arr.length - 1}
          />
        ))}
        {toggle}
      </>
    );
  }

  // The toggle trails the visible rows rather than splitting them: the run
  // reads top-to-bottom as three actions followed by a footnote for the rest,
  // and expanding it grows the list downward from where the eye already is.
  // Sitting mid-run, it broke the sequence in half at a point that means
  // nothing to the reader — the elided rows are not all in one place anyway.
  return (
    <>
      {headBlocks.map((block) => (
        <TurnBlockRenderer key={block.id} block={block} agentId={agentId} isLive={isLive} />
      ))}
      {tailBlocks.map((block, i, arr) => (
        <TurnBlockRenderer
          key={block.id}
          block={block}
          agentId={agentId}
          isLive={isLive}
          isLastBlock={isLive && i === arr.length - 1}
        />
      ))}
      {toggle}
    </>
  );
}

/**
 * A run of finished `thinking` blocks collapsed into a single "Thinking ×N"
 * row — each one otherwise renders its own truncated preview line
 * ({@link ThinkingBlockView}), and several in a row are pure repeat noise
 * with no informational difference between them at a glance.
 *
 * The run's most recent block stays on screen, so the latest thought is still
 * readable mid-stream instead of vanishing behind the counter; the toggle
 * counts only what it actually hides. Expanding shows every block's own
 * preview/expand affordance, unchanged.
 */
function CollapsedThinkingRun({ blocks, agentId }: { blocks: ThinkingBlock[]; agentId?: string }) {
  const [expanded, setExpanded] = React.useState(false);
  const hiddenBlocks = blocks.slice(0, blocks.length - THINKING_RUN_VISIBLE);
  const visibleBlocks = blocks.slice(blocks.length - THINKING_RUN_VISIBLE);

  return (
    <>
      {/* The toggle sits above the still-visible tail, matching where the
          blocks it elides would have been. */}
      {expanded &&
        hiddenBlocks.map((block) => (
          <TurnBlockRenderer key={block.id} block={block} agentId={agentId} />
        ))}
      <Button
        variant="ghost"
        onClick={() => setExpanded((v) => !v)}
        className={TOOL_GROUP_TOGGLE_CLASS}
        data-testid="thinking-group-toggle"
        aria-expanded={expanded}
        aria-label={
          expanded
            ? `Hide ${hiddenBlocks.length} thinking blocks`
            : `Show ${hiddenBlocks.length} more thinking blocks`
        }
      >
        {expanded ? (
          <>
            <ChevronDown className="size-3 shrink-0" />
            <span>Show less</span>
          </>
        ) : (
          <>
            <ChevronRight className="size-3 shrink-0" />
            <span>Thinking &times; {hiddenBlocks.length}</span>
          </>
        )}
      </Button>
      {visibleBlocks.map((block) => (
        <TurnBlockRenderer key={block.id} block={block} agentId={agentId} />
      ))}
    </>
  );
}

function isToolBlock(block: TurnBlock): boolean {
  return (
    block.type === 'tool_call' ||
    block.type === 'task' ||
    block.type === 'thinking' ||
    block.type === 'control_request' ||
    block.type === 'codex_approval' ||
    block.type === 'codex_error' ||
    block.type === 'agent_log' ||
    block.type === 'system_init' ||
    block.type === 'plan'
  );
}

function VisualGroupView({
  group,
  isLive,
  workingLabel,
  agentId,
  highlightEventId,
}: {
  group: VisualGroup;
  isLive?: boolean;
  workingLabel?: string;
  agentId?: string;
  highlightEventId?: string;
}) {
  const contentBlocks = group.blocks.filter((b) => !isToolBlock(b));
  const hasContent = contentBlocks.length > 0;

  const contentSegments = React.useMemo(() => {
    return segmentContentBlocks(contentBlocks);
  }, [contentBlocks]);

  const toolSegments = React.useMemo(() => {
    const toolBlocks = group.blocks.filter(isToolBlock);
    return segmentBlocks(toolBlocks);
  }, [group.blocks]);

  // A live collapsed run folds the `Working…` label into its own toggle (see
  // {@link CollapsedToolRun}), so the standalone row below would be a second
  // copy of the same signal.
  const lastToolSeg = toolSegments[toolSegments.length - 1];
  const endsWithLiveToolRun = isLive && lastToolSeg?.kind === 'run';

  return (
    <div>
      {hasContent && (
        <div className="space-y-1.5 py-1">
          {contentSegments.map((segment, i) => {
            if (segment.kind === 'images') {
              const images: StackImage[] = segment.blocks.map((imageBlock) => ({
                src: imageBlock.data
                  ? `data:${imageBlock.mimeType};base64,${imageBlock.data}`
                  : imageBlock.uri || '',
                alt: `Image ${i + 1}`,
              }));
              return (
                <div key={`images-${i}`}>
                  <StackedImages images={images} cardWidth={200} cardHeight={125} />
                </div>
              );
            }

            const block = segment.block;
            return (
              <TurnBlockRenderer
                key={block.id}
                block={block}
                agentId={agentId}
                isLive={isLive}
                isLastBlock={
                  isLive && i === contentSegments.length - 1 && toolSegments.length === 0
                }
              />
            );
          })}
        </div>
      )}

      {toolSegments.length > 0 && (
        <div className="my-2 ml-0.5 border-l-2 border-ring/15 pl-4">
          {toolSegments.map((seg, i) =>
            seg.kind === 'run' ? (
              <CollapsedToolRun
                key={seg.blocks[0].id}
                blocks={seg.blocks}
                isLive={isLive && i === toolSegments.length - 1}
                workingLabel={workingLabel}
                agentId={agentId}
                highlightEventId={highlightEventId}
              />
            ) : seg.kind === 'thinking_run' ? (
              <CollapsedThinkingRun key={seg.blocks[0].id} blocks={seg.blocks} agentId={agentId} />
            ) : (
              <TurnBlockRenderer
                key={seg.block.id}
                block={seg.block}
                agentId={agentId}
                isLive={isLive}
                isLastBlock={isLive && i === toolSegments.length - 1}
              />
            ),
          )}
        </div>
      )}

      {/*
        The live row always trails the group's last visible row, so the "still
        working" signal sits where the next action lands.
      */}
      {isLive && !endsWithLiveToolRun && (
        <div className={toolSegments.length > 0 ? 'ml-0.5 pl-4' : undefined}>
          <WorkingIndicator visible label={workingLabel} />
        </div>
      )}
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1_000) return `${ms}ms`;
  const secs = ms / 1_000;
  if (secs < 60) return `${secs.toFixed(1)}s`;
  const mins = Math.floor(secs / 60);
  const remSecs = Math.round(secs % 60);
  return remSecs > 0 ? `${mins}m ${remSecs}s` : `${mins}m`;
}

function formatCost(cost: number): string {
  if (cost < 0.01 && cost > 0) return '<$0.01';
  return `$${cost.toFixed(2)}`;
}

function subtypeLabel(subtype: string): string {
  switch (subtype) {
    case 'error_max_turns':
      return 'Max turns reached';
    case 'error_during_execution':
      return 'Execution error';
    default:
      return 'Error';
  }
}

const STOP_REASON_LABELS: Record<string, string> = {
  max_tokens: 'Token limit reached',
  stop_sequence: 'Stop sequence',
  cancelled: 'Interrupted',
  interrupted: 'Interrupted',
  end_turn: '',
  EndTurn: '',
  endturn: '',
};

function stopReasonBadge(reason: string): string | null {
  const label = STOP_REASON_LABELS[reason] ?? STOP_REASON_LABELS[reason.toLowerCase()];
  if (label === '') return null;
  return label ?? reason;
}

const COPY_TURN_BUTTON_CLASS =
  'inline-flex h-6 w-6 items-center justify-center rounded-md bg-background/60 text-muted-foreground backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground';

interface AssistantTurnProps {
  blocks: TurnBlock[];
  /** Session metadata to expose when this turn does not carry its own init block. */
  sessionInitBlock?: SystemInitBlock;
  isLive?: boolean;
  workingLabel?: string;
  stopReason?: string;
  isError?: boolean;
  cost?: number;
  tokenUsage?: ChatTokenUsage;
  numTurns?: number;
  durationMs?: number;
  timestamp?: number;
  sourceEvent?: AxonEvent;
  agentId?: string;
  /** Event requested by a transcript deep link. */
  highlightEventId?: string;
  /** Agent-type id of the harness running the agent (e.g. `opencode`). */
  agentType?: string | null;
  agentModel?: string | null;
}

/**
 * A prefix replay reconstructs the one session-init block and may move it
 * from the old truncated boundary turn to its true earlier owner. Every later
 * turn still receives that same durable announcement through
 * `sessionInitBlock`, but as a new block object. Shallow memo equality would
 * therefore rerender the entire completed transcript after every prepend.
 *
 * Axon events are immutable: the anchoring event id is the semantic identity
 * of a reconstructed init block. Eventless story/synthetic blocks retain
 * ordinary reference equality.
 */
function sessionInitEqual(
  previous: SystemInitBlock | undefined,
  next: SystemInitBlock | undefined,
): boolean {
  if (Object.is(previous, next)) return true;
  const previousEventId = previous?.sourceEvent?.id;
  return previousEventId !== undefined && previousEventId === next?.sourceEvent?.id;
}

export function assistantTurnPropsEqual(
  previous: AssistantTurnProps,
  next: AssistantTurnProps,
): boolean {
  return (
    previous.blocks === next.blocks &&
    sessionInitEqual(previous.sessionInitBlock, next.sessionInitBlock) &&
    previous.isLive === next.isLive &&
    previous.workingLabel === next.workingLabel &&
    previous.stopReason === next.stopReason &&
    previous.isError === next.isError &&
    previous.cost === next.cost &&
    previous.tokenUsage === next.tokenUsage &&
    previous.numTurns === next.numTurns &&
    previous.durationMs === next.durationMs &&
    previous.timestamp === next.timestamp &&
    previous.sourceEvent === next.sourceEvent &&
    previous.agentId === next.agentId &&
    previous.highlightEventId === next.highlightEventId &&
    previous.agentType === next.agentType &&
    previous.agentModel === next.agentModel
  );
}

export const AssistantTurn = React.memo(function AssistantTurn({
  blocks,
  sessionInitBlock,
  isLive,
  workingLabel,
  stopReason,
  isError,
  cost,
  tokenUsage,
  numTurns,
  durationMs,
  timestamp,
  sourceEvent,
  agentId,
  highlightEventId,
  agentType,
  agentModel,
}: AssistantTurnProps) {
  const ownInitBlock = React.useMemo(
    () => blocks.find((b): b is SystemInitBlock => b.type === 'system_init'),
    [blocks],
  );
  const initBlock = ownInitBlock ?? sessionInitBlock;
  // `system_init` renders as the popover behind the avatar rather than in the
  // body, and `rendersNothing` blocks render nowhere at all. Drop both before
  // grouping: an invisible block left in the list would still split a run of
  // tool calls and start a new visual group, breaking the turn into pieces
  // with no on-screen cause.
  // `collapseReadBeforeWriteRetries` then drops the harness's read-first
  // detour: a write refused for not having read the file, the read, and the
  // write again collapse to one marked row. It runs on the filtered list so an
  // invisible block between the three calls can't break the sequence.
  const renderableBlocks = React.useMemo(
    () =>
      collapseReadBeforeWriteRetries(
        blocks.filter((b) => b.type !== 'system_init' && !rendersNothing(b)),
      ),
    [blocks],
  );
  const visualGroups = React.useMemo(() => buildVisualGroups(renderableBlocks), [renderableBlocks]);
  const modelShortName = getModelDisplayName(agentModel);
  const estimatedCost =
    cost == null && agentType === 'codex' ? estimateCodexTurnCostUsd(agentModel, tokenUsage) : null;
  const displayedCost = cost ?? estimatedCost;

  // The agent's prose output, used as the copy-to-clipboard source. Empty for
  // tool-only / still-thinking turns, in which case the copy button is hidden.
  const copyText = React.useMemo(() => buildTurnCopyText(blocks), [blocks]);
  // Rendered on demand: parsing every turn's markdown up front would cost a
  // full transcript's worth of work for a copy that may never happen.
  const getCopyHtml = React.useCallback(() => buildTurnCopyHtml(blocks), [blocks]);

  // A skipped/dismissed AskUserQuestion ends the turn with a denied tool_use,
  // which the result reports as is_error / stop_reason "tool_use". That's a
  // deliberate user action, not a failure — don't show the red error state or
  // the tool_use badge for it. The question's card already shows the outcome.
  const endedByDeclinedQuestion = React.useMemo(
    () =>
      blocks.some(
        (b) => b.type === 'tool_call' && b.title === 'AskUserQuestion' && b.status === 'failed',
      ),
    [blocks],
  );
  const effectiveError = isError && !endedByDeclinedQuestion;

  const hasStats =
    displayedCost != null || tokenUsage != null || durationMs != null || numTurns != null;
  const showSummary = !isLive && (stopReason || isError || hasStats);
  // The footer (summary bar + copy button) belongs to a finished turn. While the
  // turn is still live, `copyText` is already populated from streamed prose, so
  // gating on `copyText` alone would surface a lone copy button and an empty
  // footer strip before the turn completes. Hold the whole footer until the turn
  // settles so the finished bottom bar appears together, naturally.
  const showFooter = !isLive && (showSummary || Boolean(copyText));
  const stopBadge = stopReason && !endedByDeclinedQuestion ? stopReasonBadge(stopReason) : null;

  const metricCount =
    (durationMs != null ? 1 : 0) +
    (displayedCost != null ? 1 : 0) +
    (tokenUsage != null ? 1 : 0) +
    (numTurns != null ? 1 : 0);

  const avatarEl = <AgentAvatar agentType={agentType} model={agentModel} />;

  return (
    <div
      className="group/msg relative"
      data-testid="assistant-turn"
      data-event-id={sourceEvent?.id}
    >
      {sourceEvent && (
        <div className="pointer-events-none absolute right-2 top-2 z-10 flex items-center gap-1 opacity-0 transition-opacity group-hover/msg:pointer-events-auto group-hover/msg:opacity-100">
          <EventDetailTooltip event={sourceEvent} />
        </div>
      )}
      <div className="flex items-start gap-2 sm:gap-3">
        {/*
          Drop the avatar so its center lands on the first line of the card
          body rather than the card's top edge. The offset is the body's top
          padding (`pt-4` = 16px) plus the inner content padding (`py-1` = 4px),
          minus half the difference between the avatar (28px) and the first
          line box (~22.75px): 16 + 4 - 2.6 ≈ 18px. This makes the avatar read
          as vertically centered on the opening line instead of floating above.
        */}
        <div className="mt-[18px] shrink-0">
          {initBlock ? (
            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <InteractiveAvatar aria-label="Session details">{avatarEl}</InteractiveAvatar>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>{modelShortName}</TooltipContent>
              </Tooltip>
              <PopoverContent
                side="bottom"
                align="start"
                className="w-[340px] max-h-[min(480px,70vh)] overflow-y-auto p-0"
              >
                <SystemInitPopoverContent
                  block={initBlock}
                  heading={ownInitBlock ? 'Session initialized' : 'Session details'}
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{avatarEl}</span>
              </TooltipTrigger>
              <TooltipContent>{modelShortName}</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="min-w-0 flex-1">
          {/* Card: body + footer */}
          <div className="rounded-2xl border border-border/30 bg-card/40 backdrop-blur-sm">
            <div className="px-3 pb-3.5 pt-4 sm:px-5">
              {visualGroups.map((group, gi) => (
                <VisualGroupView
                  key={group.id}
                  group={group}
                  isLive={isLive && gi === visualGroups.length - 1}
                  workingLabel={workingLabel}
                  agentId={agentId}
                  highlightEventId={highlightEventId}
                />
              ))}

              {/*
                Empty-body fallback: render the working row while
                the turn is live but has nothing visible yet. Gated on
                `visualGroups.length === 0` (not `blocks.length === 0`)
                so a turn holding only filtered-out blocks — a
                `system_init`, or a text block whose first delta has not
                landed — still shows the indicator instead of an empty
                card.
              */}
              {isLive && visualGroups.length === 0 && (
                <WorkingIndicator visible label={workingLabel} />
              )}
            </div>

            {showFooter && (
              <div className="border-t border-border/20 px-3 py-2 text-xs text-muted-foreground/60 sm:px-5 sm:py-2.5">
                <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3.5">
                  {showSummary && (
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                      {effectiveError ? (
                        <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-status-error">
                          <XCircle className="h-3 w-3" />
                          <span className="font-medium">{subtypeLabel(stopReason ?? 'error')}</span>
                        </span>
                      ) : (
                        <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-status-success">
                          <CheckCircle2 className="h-3 w-3" />
                          <span className="font-medium">Done</span>
                        </span>
                      )}

                      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 sm:gap-x-2">
                        {modelShortName && (
                          <span className="whitespace-nowrap font-medium text-foreground/70">
                            {modelShortName}
                          </span>
                        )}
                        {modelShortName && metricCount > 0 && (
                          <span aria-hidden className="text-muted-foreground/30">
                            &middot;
                          </span>
                        )}
                        {durationMs != null && (
                          <span className="whitespace-nowrap tabular-nums">
                            {formatDuration(durationMs)}
                          </span>
                        )}
                        {durationMs != null && displayedCost != null && (
                          <span aria-hidden className="text-muted-foreground/30">
                            &middot;
                          </span>
                        )}
                        {displayedCost != null && (
                          <span
                            className="whitespace-nowrap tabular-nums"
                            title={
                              estimatedCost != null
                                ? 'Estimated from published token rates'
                                : undefined
                            }
                          >
                            {formatCost(displayedCost)}
                          </span>
                        )}
                        {(durationMs != null || displayedCost != null) && tokenUsage != null && (
                          <span aria-hidden className="text-muted-foreground/30">
                            &middot;
                          </span>
                        )}
                        {tokenUsage != null && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="whitespace-nowrap tabular-nums">
                                {formatTokenCount(tokenUsage.totalTokens)} tokens
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {`${tokenUsage.inputTokens.toLocaleString()} in (${tokenUsage.cachedInputTokens.toLocaleString()} cached) · ${tokenUsage.outputTokens.toLocaleString()} out`}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {(durationMs != null || displayedCost != null || tokenUsage != null) &&
                          numTurns != null && (
                            <span aria-hidden className="text-muted-foreground/30">
                              &middot;
                            </span>
                          )}
                        {numTurns != null && (
                          <span className="whitespace-nowrap tabular-nums">
                            {numTurns} {numTurns === 1 ? 'turn' : 'turns'}
                          </span>
                        )}
                      </div>

                      {stopBadge && (
                        <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-status-warning">
                          <AlertTriangle className="h-3 w-3" />
                          <span>{stopBadge}</span>
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex shrink-0 items-center justify-end gap-2 sm:ml-auto">
                    {copyText && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SharedCopyButton
                            text={copyText}
                            getHtml={getCopyHtml}
                            ariaLabel="Copy message"
                            copiedAriaLabel="Copied message"
                            testId="copy-turn-button-bottom"
                            className={`${COPY_TURN_BUTTON_CLASS} bg-transparent backdrop-blur-none hover:bg-muted/60 opacity-0 transition-opacity group-hover/msg:opacity-100`}
                          />
                        </TooltipTrigger>
                        <TooltipContent>Copy</TooltipContent>
                      </Tooltip>
                    )}

                    {timestamp != null && (
                      <span
                        className="shrink-0 whitespace-nowrap tabular-nums"
                        data-testid="turn-footer-timestamp"
                      >
                        {formatDateTimeWithSeconds(timestamp)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}, assistantTurnPropsEqual);

interface AssistantMessageProps {
  message: ChatMessage;
  agentId?: string;
  agentType?: string | null;
  agentModel?: string | null;
}

export function AssistantMessage({
  message,
  agentId,
  agentType,
  agentModel,
}: AssistantMessageProps) {
  return (
    <AssistantTurn
      blocks={message.blocks ?? []}
      isLive={false}
      stopReason={message.stopReason}
      isError={message.isError}
      cost={message.cost}
      numTurns={message.numTurns}
      durationMs={message.durationMs}
      timestamp={message.timestamp}
      sourceEvent={message.sourceEvent}
      agentId={agentId}
      agentType={agentType}
      agentModel={agentModel}
    />
  );
}
